import React, { useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Form, Select } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function IrenaCountryMappingTab({ countryMappings, onRefreshCountryMappings }) {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const countriesState = useSelector((state) => state.countries);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const data = countryMappings || [];

  const handleDelete = (irena_code) => () => {
    Modal.confirm({
      title: 'Delete IRENA Country Mapping',
      content: `Are you sure you want to delete the mapping for ${irena_code}?`,
      okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        try {
          await AxiosOICStat.delete(`/external_api/irena/country-mappings`, { data: { geo_area_code: irena_code } });
          onRefreshCountryMappings();
          message.success('Deleted mapping');
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Failed to delete mapping.';
          message.error(detail);
        }
      }
    });
  };

  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      setLoading(true);
      if (editMode && editingRecord) {
        await AxiosOICStat.put("/external_api/irena/country-mappings", { geo_area_code: values.irena_code, geo_area_name: values.geo_area, c_code: values.c_code });
        message.success("Country mapping updated successfully");
      } else {
        await AxiosOICStat.post("/external_api/irena/country-mappings", { geo_area_code: values.irena_code, geo_area_name: values.geo_area, c_code: values.c_code });
        message.success("Country mapping added successfully");
      }

      setAddModalVisible(false);
      addForm.resetFields();
      setEditMode(false);
      setEditingRecord(null);
      onRefreshCountryMappings();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to add mapping.';
      message.error(detail);
    } finally { setLoading(false); }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditMode(true);
    setAddModalVisible(true);
    addForm.setFieldsValue({ irena_code: record.irena_code, geo_area: record.geo_area, c_code: record.c_code });
  };

  const columns = [
    { title: 'IRENA Code', dataIndex: 'irena_code', key: 'irena_code', render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text> },
    { title: 'Geo Area Name', dataIndex: 'geo_area', key: 'geo_area', render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text> },
    { title: 'OIC Country Code', dataIndex: 'c_code', key: 'c_code', render: (v) => <Text strong>{v}</Text> },
    { title: 'Actions', key: 'actions', render: (_, record) => (
      <Space>
        <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
        <Button type="primary" danger icon={<DeleteOutlined />} size="small" onClick={handleDelete(record.irena_code)} />
      </Space>
    )}
  ];

  const availableCountries = countriesState.value
    ? Object.values(countriesState.value)
        .map(c => ({
          ...c,
          displayName: c.c_short_name_eng || c.c_full_name_eng || c.c_name_eng || c.c_name || ''
        }))
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
    : [];

  const filteredData = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return data;
    return data.filter(item => {
      const irenaCode = (item.irena_code || '').toLowerCase();
      const geo = (item.geo_area || '').toLowerCase();
      const ccode = String(item.c_code || '');
      return irenaCode.includes(q) || geo.includes(q) || ccode.includes(q);
    });
  }, [data, query]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Search allowClear placeholder="Search by IRENA code / geo area / country code…" onSearch={setQuery} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 420 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditMode(false); setEditingRecord(null); setAddModalVisible(true); }}>Add New Country Mapping</Button>
      </Space>

      <Table rowKey={(r) => r.irena_code || r.id} loading={loading} columns={columns} dataSource={filteredData} pagination={{ pageSize: 20, showSizeChanger: true }} bordered />

      <Modal
        title={editMode ? 'Edit IRENA Country Mapping' : 'Add IRENA Country Mapping'}
        visible={addModalVisible}
        onOk={handleAddSubmit}
        onCancel={() => { addForm.resetFields(); setAddModalVisible(false); setEditMode(false); setEditingRecord(null); }}
        confirmLoading={loading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="irena_code" label="IRENA Code" rules={[{ required: true, message: 'Please enter the IRENA country code' }]}>
            <Input placeholder="e.g. TUR, AFG, SAU" />
          </Form.Item>
          <Form.Item name="geo_area" label="Geo Area Name">
            <Input placeholder="Country name from IRENA" />
          </Form.Item>
          <Form.Item name="c_code" label="OIC Country Code" rules={[{ required: true, message: 'Please select an OIC country' }]}>
            <Select showSearch allowClear placeholder="Select OIC country" optionFilterProp="children">
              {availableCountries.map(c => (
                <Option key={c.c_code} value={c.c_code}>{c.c_code} - {c.displayName}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
